import { useState } from 'react';
import { api } from '../../core/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { RotateCcw } from 'lucide-react';

interface Movement {
  je_date?: string;
  je_number?: string;
  je_description?: string;
  coa_code?: string;
  coa_name?: string;
  jel_debit?: number;
  jel_credit?: number;
}

interface ReportData {
  success: boolean;
  movements: Movement[];
  summary?: Record<string, any>;
  totalDebits: number;
  totalCredits: number;
}

export default function Reports() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const formatDate = (date: Date) => date.toISOString().split('T')[0];

  const [filters, setFilters] = useState({
    accountType: '',
    dateFrom: formatDate(thirtyDaysAgo),
    dateTo: formatDate(today),
    journal: '',
    status: '',
  });

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const response = await api.post<ReportData>('/comptabilitat/reports/custom', filters);

      if (response?.success) {
        setReportData(response);
      } else {
        alert('Error: No s\'ha pogut generar l\'informe');
      }
    } catch (error: any) {
      alert('Error: ' + (error?.message || 'Error desconegut'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      accountType: '',
      dateFrom: formatDate(thirtyDaysAgo),
      dateTo: formatDate(today),
      journal: '',
      status: '',
    });
    setReportData(null);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Informes Comptables</h1>
        <p className="text-gray-600">Genera informes personalitzats de moviments comptables</p>
      </div>

      <Card className="p-6">
        <h2 className="text-sm font-semibold mb-4">Generar Informe Personalitzat</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-1">Tipus de Compte</label>
            <select
              value={filters.accountType}
              onChange={(e) => setFilters({ ...filters, accountType: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Totes</option>
              <option value="Activo">Actiu</option>
              <option value="Pasivo">Passiu</option>
              <option value="Patrimonio">Patrimoni</option>
              <option value="Ingreso">Ingrés</option>
              <option value="Gasto">Despesa</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Diari</label>
            <select
              value={filters.journal}
              onChange={(e) => setFilters({ ...filters, journal: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Tots</option>
              <option value="1">General</option>
              <option value="2">Vendes</option>
              <option value="3">Compres</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Des de</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Fins a</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Estat</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Tots</option>
              <option value="Borrador">Esborrany</option>
              <option value="Aprobado">Aprovat</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleGenerateReport} disabled={loading} className="bg-blue-600 hover:bg-gray-700">
            {loading ? 'Generant...' : 'Generar Informe'}
          </Button>
          <Button onClick={handleResetFilters} variant="outline" className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Netejar Filtres
          </Button>
        </div>
      </Card>

      {reportData && (
        <Card className="p-6">
          <div className="bg-green-50 border border-green-200 rounded p-4 mb-6">
            <p className="text-green-700 font-semibold">✓ Informe generat correctament</p>
            <div className="text-sm text-green-600 mt-2 space-y-1">
              <p>Moviments trobats: {reportData.movements?.length || 0}</p>
              <p>Total Dèbits: {(reportData.totalDebits || 0).toFixed(2)}</p>
              <p>Total Crèdits: {(reportData.totalCredits || 0).toFixed(2)}</p>
            </div>
          </div>

          {reportData.movements && reportData.movements.length > 0 && (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold">Data</th>
                    <th className="px-4 py-2 text-left font-semibold">Número</th>
                    <th className="px-4 py-2 text-left font-semibold">Descripció</th>
                    <th className="px-4 py-2 text-left font-semibold">Codi</th>
                    <th className="px-4 py-2 text-left font-semibold">Compte</th>
                    <th className="px-4 py-2 text-right font-semibold">Deure</th>
                    <th className="px-4 py-2 text-right font-semibold">Haver</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.movements.map((movement, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {typeof movement.je_date === 'string'
                          ? movement.je_date.substring(0, 10)
                          : movement.je_date
                            ? (() => {
                                try {
                                  const date = new Date(movement.je_date);
                                  if (isNaN(date.getTime())) return '-';
                                  return date.toISOString().substring(0, 10);
                                } catch {
                                  return '-';
                                }
                              })()
                            : '-'
                        }
                      </td>
                      <td className="px-4 py-2">{movement.je_number || '-'}</td>
                      <td className="px-4 py-2">{movement.je_description || '-'}</td>
                      <td className="px-4 py-2 font-mono text-xs">{movement.coa_code || '-'}</td>
                      <td className="px-4 py-2">{movement.coa_name || '-'}</td>
                      <td className="px-4 py-2 text-right">{Number(movement.jel_debit || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">{Number(movement.jel_credit || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                    <td colSpan={5} className="px-4 py-2 text-right">TOTALS:</td>
                    <td className="px-4 py-2 text-right">{(reportData.totalDebits || 0).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right">{(reportData.totalCredits || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {!reportData && !loading && (
        <Card className="p-12 text-center">
          <p className="text-gray-500">Fes clic a &quot;Generar Informe&quot; per veure els resultats</p>
        </Card>
      )}
    </div>
  );
}
